import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { useAgentStore } from './agentStore.js'

export type User = {
  /** Subject — the user's UUID. Maps to `id` in the login response. */
  sub: string
  id: string
  /** The current agent's agents.id (resolved from GET /agent/me), cached
   *  once per session — distinct from `sub`/`id`, which are users.id. */
  agentId?: string
  tenantId: string
  tenantSlug: string
  roles: string[]
  permissions: string[]
  email?: string | null
  /** Display name from the tenant users row (may be null until profile sync) */
  fullName?: string | null
  tenantFeatureFlags?: Record<string, boolean>
  queueMemberships?: string[]
  /** Agent extension number (from agents table) */
  extension?: string
  /** Login ID / username */
  loginId?: string | null
  /** Whether telephony is allowed for this user */
  allowTelephony?: boolean
  /** Whether agent recording pause/resume is allowed for this tenant */
  allowAgentPause?: boolean | undefined
  /** Whether call recording is enabled for this tenant */
  recordingEnabled?: boolean
  /**
   * IANA timezone from the user's profile. Used as the default for every
   * rendered timestamp when the header dropdown is left on "Automatic"
   * (see `utils/timezone.ts`).
   */
  timezone?: string | null
  /** ITEM 99 — the agent's auto-answer preference, from /auth/me. */
  autoAnswerIncomingCalls?: boolean
  /**
   * `users.user_type` — 'pbx' | 'callcenter'.
   *
   * The column has existed since provisioning and never reached the client, so
   * the agent workspace could not tell the two kinds of user apart: a PBX
   * extension user was shown ACD Ready/Idle, queue status and campaign
   * controls that do not apply to them.
   *
   * Absent means 'pbx' — the narrower feature set is the safe default for a
   * session that predates this field.
   */
  userType?: 'pbx' | 'callcenter'
  isSuperAdmin?: boolean
  /** Sidebar menu permissions — array of route paths the user can see. Empty/null = all visible. */
  sidebarPermissions?: string[]
  /**
   * Which login this session came from: 'admin' (/login/platform) or 'agent'
   * (/login). An admin session does not carry into the agent portal — see
   * requireAuth.
   */
  portal?: 'admin' | 'agent'
}

/**
 * SW-15: the only fields written to localStorage. Permissions, feature flags, queue
 * memberships, e-mail, extension etc. are NOT persisted; they are re-read from
 * GET /auth/me when a tab opens (see auth/requireAuth.ts). The CSRF token is held in
 * memory only for the same reason.
 */
const PERSISTED_USER_KEYS = [
  'sub', 'id', 'agentId', 'tenantId', 'tenantSlug', 'roles', 'fullName', 'loginId', 'userType', 'isSuperAdmin', 'portal',
  'allowTelephony', 'allowAgentPause', 'recordingEnabled', 'extension',
] as const

export function toPersistedUser(user: User | null | undefined): User | null {
  if (!user) return null
  const out: Record<string, unknown> = { permissions: [] }
  for (const k of PERSISTED_USER_KEYS) {
    if ((user as Record<string, unknown>)[k] !== undefined) out[k] = (user as Record<string, unknown>)[k]
  }
  if (!Array.isArray(out['roles'])) out['roles'] = []
  return out as unknown as User
}

interface AuthState {
  user: User | null
  csrfToken: string | null
  hasHydrated: boolean
  setSession: (s: { user: User; csrfToken: string }) => void
  setAgentId: (agentId: string) => void
  clearSession: () => void
  hasPermission: (permission: string) => boolean
  setHasHydrated: (hydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      csrfToken: null,
      hasHydrated: false,
      setSession: (s) => set({ user: s.user, csrfToken: s.csrfToken }),
      setAgentId: (agentId) =>
        set((state) => (state.user ? { user: { ...state.user, agentId } } : state)),
      clearSession: () => {
        // Clear agent store first (records logout history, clears state)
        try {
          const agentState = useAgentStore.getState()
          if (agentState.sessionHistory.length > 0) {
            agentState.logout(true) // forcefully (server restart / cleanup)
          } else {
            agentState.reset()
          }
        } catch {
          // If agent store fails, still clear localStorage
          localStorage.removeItem('agentSessionHistory')
        }
        set({ user: null, csrfToken: null })
      },
      hasPermission: (permission: string) => {
        const user = get().user
        if (!user) return false
        if (user.isSuperAdmin) return true
        if (user.roles?.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN')) return true
        const perms = user.permissions || []
        if (perms.includes('*')) return true
        return perms.includes(permission)
      },
      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'kc3-auth-storage',
      partialize: (state) => ({ user: toPersistedUser(state.user) }),
      // Entries written before SW-15 hold the full user and the CSRF token: keep only the
      // reduced user (the next write replaces the old entry) and never restore a CSRF token.
      merge: (persisted, current) => ({
        ...current,
        user: toPersistedUser((persisted as { user?: User | null } | undefined)?.user),
        csrfToken: null,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

/** Resolves once the persisted auth state has been rehydrated from storage. */
export function waitForAuthHydration(): Promise<void> {
  if (useAuthStore.getState().hasHydrated) return Promise.resolve()
  return new Promise((resolve) => {
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.hasHydrated) {
        unsubscribe()
        resolve()
      }
    })
  })
}
